import React, { useId, useState } from "react";
import { Trans } from "react-i18next";

import { useTranslation } from "lib/i18n/use-translation";

import { ACCOUNT_MANAGER } from "../contact";
import { useDashboardTracking } from "../tracking";
import styles from "./business-impact.module.scss";
import BusinessRiskInsights from "./BusinessRiskInsights";
import PilotSummary from "./PilotSummary";
import RoiSummary from "./RoiSummary";
import { BUSINESS_IMPACT_SAMPLES, BusinessImpactVersion } from "./sample";
import ScenarioModeller from "./ScenarioModeller";
import StressLevelChanges from "./StressLevelChanges";

const K = "admin:companyDashboard.businessImpact";

const VERSIONS: BusinessImpactVersion[] = ["full", "projected", "pilot"];

const WHITEPAPER_URL =
  "https://wone-public-images.s3.eu-west-2.amazonaws.com/whitepaper/WONE_Whitepaper_Risky_Business.pdf";

const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path
      d="M10 3v9m0 0-3.5-3.5M10 12l3.5-3.5M4 14v2h12v-2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BusinessImpactTab = () => {
  const { t } = useTranslation(["admin"]);
  const { filterChanged, contactClicked } = useDashboardTracking();
  const [version, setVersion] = useState<BusinessImpactVersion>("full");
  const sample = BUSINESS_IMPACT_SAMPLES[version];
  const soonId = useId();
  const viewingId = useId();
  const hintId = useId();
  const chooseVersion = (next: BusinessImpactVersion) => {
    if (next === version) return;
    filterChanged("business_impact", "version");
    setVersion(next);
  };

  return (
    <div className={styles["business-impact"]}>
      <header className={styles["header"]}>
        <div className={styles["header-text"]}>
          <div className={styles["title-row"]}>
            <h2 className={styles["heading"]}>{t(`${K}.title`)}</h2>
            <span className={styles["badge"]}>{t(`${K}.badge`)}</span>
          </div>
          <p className={styles["subheading"]}>{t(`${K}.subtitle`)}</p>
        </div>
        <div className={styles["viewing"]}>
          <span id={viewingId} className={styles["viewing-label"]}>
            {t(`${K}.viewing.label`)}
          </span>
          <div
            role="group"
            aria-labelledby={viewingId}
            aria-describedby={hintId}
            className={styles["segments"]}
          >
            {VERSIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={option === version}
                className={styles["segment"]}
                onClick={() => chooseVersion(option)}
              >
                {t(`${K}.viewing.options.${option}`)}
              </button>
            ))}
          </div>
          <p id={hintId} className={styles["viewing-hint"]}>
            {t(`${K}.viewing.hints.${version}`)}
          </p>
        </div>
      </header>

      {sample.version === "pilot" ? (
        <PilotSummary sample={sample} />
      ) : (
        <RoiSummary sample={sample} />
      )}

      {sample.version !== "pilot" && <StressLevelChanges sample={sample} />}
      <BusinessRiskInsights sample={sample} />

      <section className={styles["share-box"]}>
        <div>
          <h3 className={styles["share-box-title"]}>{t(`${K}.share.title`)}</h3>
          <p className={styles["share-box-body"]}>{t(`${K}.share.body`)}</p>
        </div>
        <div className={styles["share-box-action"]}>
          <button
            type="button"
            className={styles["cta"]}
            disabled
            aria-describedby={soonId}
          >
            <DownloadIcon />
            {t(`${K}.share.button`)}
          </button>
          <span id={soonId} className={styles["share-box-soon"]}>
            {t(`${K}.share.soon`)}
          </span>
        </div>
      </section>

      {sample.version !== "pilot" && (
        <ScenarioModeller key={version} sample={sample} />
      )}

      <section className={styles["manager"]}>
        <div className={styles["manager-who"]}>
          <span className={styles["avatar"]} aria-hidden="true">
            {ACCOUNT_MANAGER.initials}
          </span>
          <div>
            <p className={styles["manager-eyebrow"]}>
              {t(`${K}.manager.eyebrow`)}
            </p>
            <p className={styles["manager-name"]}>{ACCOUNT_MANAGER.name}</p>
            <a
              className={styles["manager-email"]}
              href={`mailto:${ACCOUNT_MANAGER.email}`}
              onClick={() => contactClicked("business_impact", "email")}
            >
              {ACCOUNT_MANAGER.email}
            </a>
          </div>
        </div>
        <div className={styles["manager-cta"]}>
          <p>{t(`${K}.manager.body`)}</p>
          <a
            className={styles["cta"]}
            href={`mailto:${ACCOUNT_MANAGER.email}`}
            onClick={() => contactClicked("business_impact", "invest")}
          >
            {t(`${K}.manager.cta`)}
          </a>
        </div>
      </section>

      <p className={styles["footnote"]}>
        <Trans
          i18nKey={`${K}.footnote`}
          components={{
            // Trans swaps the placeholder for the catalog's link text; it is
            // what jsx-a11y/anchor-has-content reads statically.
            whitepaper: (
              <a
                href={WHITEPAPER_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {"link"}
              </a>
            ),
            hidden: <span className={styles["visually-hidden"]} />,
            arrow: <span aria-hidden="true" />,
          }}
        />
      </p>
    </div>
  );
};

export default BusinessImpactTab;
